import { formatAccountAmount, formatSpacedIdentifier, getCurrencySymbol, getPrimaryDepositField } from './accountsHelpers';
import {
  getStatusHeroMeta,
  isIncomingTransaction,
} from './transactionDetailHelpers';
import { formatPaymentPurpose, toTransactionList } from './recipientHelpers';

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatGeneratedAt(dateInput = new Date()) {
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function formatStatementDate(dateInput) {
  if (!dateInput) return '—';
  const date = new Date(dateInput);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

function formatStatementDateTime(dateInput) {
  if (!dateInput) return '—';
  const date = new Date(dateInput);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function formatMoney(amount, currency, { signed = false } = {}) {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '—';
  const formatted = formatAccountAmount(Math.abs(value), currency);
  if (!signed) return formatted;
  if (value > 0) return `+${formatted}`;
  if (value < 0) return `−${formatted}`;
  return formatted;
}

function getTxDate(tx) {
  return tx?.started || tx?.dateAdded || tx?.finished || null;
}

export const STATEMENT_FILTERS = [
  { id: 'all', label: 'All settled' },
  { id: 'month', label: 'This month' },
  { id: '30d', label: 'Last 30 days' },
  { id: '90d', label: 'Last 90 days' },
];

const SETTLED_STATUSES = new Set(['Completed']);

export function getStatementFilterLabel(filterId = 'all') {
  return STATEMENT_FILTERS.find((item) => item.id === filterId)?.label || STATEMENT_FILTERS[0].label;
}

function matchesStatementFilter(transaction, filterId = 'all') {
  // Statements only include posted activity so money in/out matches wallet movement.
  if (!SETTLED_STATUSES.has(transaction?.status)) return false;

  if (!filterId || filterId === 'all') return true;

  const rawDate = getTxDate(transaction);
  if (!rawDate) return false;
  const date = new Date(rawDate);
  if (Number.isNaN(date.getTime())) return false;

  const now = new Date();
  if (filterId === 'month') {
    return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
  }

  let days = null;
  if (filterId === '30d') days = 30;
  else if (filterId === '90d') days = 90;
  if (days == null) return true;
  const cutoff = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  return date >= cutoff;
}

function normalizeCurrencyCode(value) {
  const raw = String(value || '').toUpperCase().replace(/[^A-Z]/g, '');
  if (!raw) return '';
  if (raw === 'USDC' || raw === 'USDT') return 'USD';
  if (raw === 'EURC' || raw === 'EUROC') return 'EUR';
  if (raw.length >= 3) return raw.slice(0, 3);
  return raw;
}

function getCurrencyPair(transaction) {
  return String(transaction?.currencyPair || transaction?.CurrencyPair || '')
    .toUpperCase()
    .replace(/[^A-Z]/g, '');
}

function getBaseCurrency(transaction) {
  const explicit = normalizeCurrencyCode(transaction?.baseCurrency || transaction?.BaseCurrency);
  if (explicit) return explicit;

  const pair = getCurrencyPair(transaction);
  if (pair.length >= 3) return normalizeCurrencyCode(pair.slice(0, 3));

  // Transaction history UI treats activity as the EUR wallet when send currency is omitted.
  return 'EUR';
}

function getQuoteCurrency(transaction) {
  const explicit = normalizeCurrencyCode(transaction?.quoteCurrency || transaction?.QuoteCurrency);
  if (explicit) return explicit;

  const pair = getCurrencyPair(transaction);
  if (pair.length >= 6) return normalizeCurrencyCode(pair.slice(3, 6));
  return '';
}

export function transactionAffectsCurrency(transaction, currency) {
  const code = normalizeCurrencyCode(currency);
  if (!code || !transaction) return false;
  const base = getBaseCurrency(transaction);
  const quote = getQuoteCurrency(transaction);

  if (isIncomingTransaction(transaction)) {
    return base === code || quote === code;
  }

  // Match History: outbound sends always debit the send/base currency wallet.
  return base === code;
}

function getLedgerAmount(transaction, currency) {
  const code = String(currency || '').toUpperCase();
  const base = getBaseCurrency(transaction);
  const quote = getQuoteCurrency(transaction);
  const incoming = isIncomingTransaction(transaction);
  const fee = Number(transaction.totalFee) || 0;
  const amountIn = Math.abs(Number(transaction.amountIn ?? transaction.amountSent)) || 0;
  const amountOut = Math.abs(Number(transaction.amountOut ?? transaction.amountReceived)) || 0;

  if (incoming) {
    if (base === code && amountIn > 0) return { credit: amountIn, debit: 0 };
    if (quote === code && amountOut > 0) return { credit: amountOut, debit: 0 };
    if (base === code) return { credit: amountIn, debit: 0 };
    return { credit: 0, debit: 0 };
  }

  if (base === code) {
    return { credit: 0, debit: amountIn + fee };
  }
  return { credit: 0, debit: 0 };
}

function getCounterparty(transaction) {
  if (isIncomingTransaction(transaction)) {
    return transaction.recipient?.name || transaction.senderName || transaction.counterpartyName || 'Deposit';
  }
  return transaction.recipient?.name || 'Transfer';
}

function getDescription(transaction) {
  if (transaction.paymentReference?.trim()) return transaction.paymentReference.trim();
  if (transaction.paymentPurpose) return formatPaymentPurpose(transaction.paymentPurpose);
  if (isIncomingTransaction(transaction)) return 'Incoming funds';
  return 'Outgoing transfer';
}
export function getStatementAccountIdentity(account) {
  const rails = Array.isArray(account?.rails) ? account.rails : [];
  const preferredType = String(account?.preferredRailType || '').toLowerCase();
  const ordered = [
    ...(preferredType
      ? rails.filter((rail) => String(rail?.type || '').toLowerCase() === preferredType)
      : []),
    ...rails,
    account?.depositAccount ? { bankAccount: account.depositAccount } : null,
  ].filter(Boolean);

  const seen = new Set();
  const primary = ordered
    .map((rail) => rail?.bankAccount || rail)
    .filter((bank) => bank && typeof bank === 'object')
    .filter((bank) => {
      const key = bank.iban || bank.accountNumber || bank.id;
      if (!key) return true;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((bank) => getPrimaryDepositField(bank))
    .find((field) => field?.value);

  if (primary?.value) {
    return {
      label: primary.label,
      value: primary.value,
      raw: primary.copyValue || String(primary.value).replace(/\s/g, ''),
    };
  }

  const identifier = String(account?.identifier || '').trim();
  if (identifier && !/pay-in methods/i.test(identifier)) {
    const looksLikeIban = /^[A-Z]{2}\d/i.test(identifier.replace(/\s/g, ''));
    return {
      label: looksLikeIban ? 'IBAN' : 'Account',
      value: looksLikeIban ? formatSpacedIdentifier(identifier) : identifier,
      raw: identifier.replace(/\s/g, ''),
    };
  }

  return null;
}

export function buildAccountStatementRows(account, transactions, { filter = 'all' } = {}) {
  const currency = String(account?.currency || '').toUpperCase();
  const list = toTransactionList(transactions)
    .filter((tx) => transactionAffectsCurrency(tx, currency))
    .filter((tx) => matchesStatementFilter(tx, filter))
    .sort((a, b) => {
      const da = new Date(getTxDate(a) || 0).getTime();
      const db = new Date(getTxDate(b) || 0).getTime();
      return da - db;
    });

  return list.map((tx) => {
    const { credit, debit } = getLedgerAmount(tx, currency);
    const statusMeta = getStatusHeroMeta(tx.status);
    return {
      date: getTxDate(tx),
      counterparty: getCounterparty(tx),
      description: getDescription(tx),
      status: statusMeta?.label || tx.status || '—',
      credit,
      debit,
      incoming: isIncomingTransaction(tx),
      quoteAmount: tx.amountOut ?? tx.amountReceived,
      quoteCurrency: getQuoteCurrency(tx),
    };
  });
}

export function buildAccountStatementHtml(
  account,
  transactions = [],
  { filter = 'all', accountOwner = null, accountManager = null, contactPerson = null } = {},
) {
  const currency = String(account?.currency || '').toUpperCase();
  const title = account?.title || `${currency} Account`;
  const generatedAt = formatGeneratedAt(new Date());
  const filterLabel = getStatementFilterLabel(filter);
  const rows = buildAccountStatementRows(account, transactions, { filter });
  const closingBalance = Number(account?.amount) || 0;
  const accountIdentity = getStatementAccountIdentity(account);

  // Prefer explicit accountManager; fall back to legacy contactPerson shape.
  const manager = accountManager || contactPerson;
  const ownerName = String(accountOwner || '').trim();
  const managerName = String(manager?.name || '').trim();
  const managerEmail = String(manager?.email || '').trim();

  const totalCredits = rows.reduce((sum, row) => sum + (Number(row.credit) || 0), 0);
  const totalDebits = rows.reduce((sum, row) => sum + (Number(row.debit) || 0), 0);
  const openingBalance = closingBalance - totalCredits + totalDebits;

  const dates = rows.map((row) => row.date).filter(Boolean).map((d) => new Date(d).getTime()).filter((t) => !Number.isNaN(t));
  const periodStart = dates.length ? formatStatementDate(new Date(Math.min(...dates))) : null;
  const periodEnd = dates.length ? formatStatementDate(new Date(Math.max(...dates))) : formatStatementDate(new Date());
  const periodLabel = periodStart ? `${periodStart} – ${periodEnd}` : `As of ${periodEnd}`;

  const tableRows =
    rows.length > 0
      ? rows
          .map((row) => {
            const moneyIn = row.credit > 0 ? escapeHtml(formatMoney(row.credit, currency)) : '—';
            const moneyOut = row.debit > 0 ? escapeHtml(formatMoney(row.debit, currency)) : '—';
            const secondary =
              !row.incoming && row.quoteAmount != null && row.quoteCurrency
                ? `<div class="muted">Received ${escapeHtml(formatMoney(row.quoteAmount, row.quoteCurrency))}</div>`
                : '';
            return `
            <tr>
              <td>
                <div>${escapeHtml(formatStatementDateTime(row.date))}</div>
              </td>
              <td>
                <div class="strong">${escapeHtml(row.counterparty)}</div>
                <div class="muted">${escapeHtml(row.description)}</div>
                ${secondary}
              </td>
              <td><span class="status">${escapeHtml(row.status)}</span></td>
              <td class="num credit">${moneyIn}</td>
              <td class="num debit">${moneyOut}</td>
            </tr>`;
          })
          .join('')
      : `
        <tr>
          <td colspan="5" class="empty-row">No transactions found for this ${escapeHtml(currency)} account yet.</td>
        </tr>`;

  const symbol = getCurrencySymbol(currency);
  const closingLabel = account?.amountLabel || formatMoney(closingBalance, currency);

  const ownerHtml = ownerName ? `<div class="meta-owner">${escapeHtml(ownerName)}</div>` : '';

  const identityHtml = accountIdentity
    ? `<div class="meta-iban">${escapeHtml(accountIdentity.label)} ${escapeHtml(accountIdentity.value)}</div>`
    : '';

  const managerHtml =
    managerName || managerEmail
      ? `<div class="meta-manager">
          ${
            managerName
              ? `<div class="meta-manager-line">Account Manager: ${escapeHtml(managerName)}</div>`
              : `<div class="meta-manager-line">Account Manager</div>`
          }
          ${managerEmail ? `<div class="meta-manager-email">${escapeHtml(managerEmail)}</div>` : ''}
        </div>`
      : '';

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(title)} · Account statement</title>
    <style>
      @page { margin: 14mm 12mm; size: A4; }
      * { box-sizing: border-box; }
      body {
        margin: 0;
        padding: 24px;
        color: #0b1929;
        background: #eef3f8;
        font-family: "Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
        font-size: 12.5px;
        line-height: 1.45;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .toolbar {
        max-width: 920px;
        margin: 0 auto 16px;
        display: flex;
        justify-content: flex-end;
      }
      .toolbar button {
        border: 1px solid rgba(46, 107, 173, 0.28);
        background: #0b1929;
        color: #fff;
        border-radius: 999px;
        padding: 10px 16px;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
      }
      .sheet {
        max-width: 920px;
        margin: 0 auto;
        background: #fff;
        border: 1px solid rgba(11, 25, 41, 0.08);
        border-radius: 16px;
        padding: 34px 36px;
        box-shadow: 0 8px 28px rgba(11, 25, 41, 0.06);
      }
      .header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 24px;
        padding-bottom: 20px;
        border-bottom: 2px solid #0b1929;
        margin-bottom: 22px;
      }
      .brand {
        font-family: Georgia, "Times New Roman", serif;
        font-size: 28px;
        font-weight: 400;
        letter-spacing: -0.02em;
        margin: 0;
      }
      .brand-sub {
        margin: 4px 0 0;
        font-size: 11px;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        color: #64748b;
      }
      .meta {
        text-align: right;
        font-size: 12px;
        color: #475569;
      }
      .meta strong {
        display: block;
        color: #0b1929;
        font-size: 14px;
        font-weight: 600;
        margin-bottom: 4px;
      }
      .meta-owner {
        margin: 2px 0 6px;
        color: #0b1929;
        font-size: 13px;
        font-weight: 600;
      }
      .meta-iban {
        margin: 0 0 10px;
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        font-size: 12px;
        color: #0b1929;
        letter-spacing: 0.02em;
      }
      .meta-manager {
        margin: 0 0 10px;
      }
      .meta-manager-line {
        color: #0b1929;
        font-weight: 600;
        font-size: 12.5px;
      }
      .meta-manager-email {
        color: #475569;
        font-size: 12px;
        margin-top: 2px;
      }
      .meta-period {
        margin-top: 2px;
      }
      .summary {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 12px;
        margin-bottom: 22px;
      }
      .summary-card {
        border: 1px solid #e2e8f0;
        border-radius: 12px;
        padding: 14px 16px;
        background: #f8fafc;
      }
      .summary-card.primary {
        background: #0b1929;
        border-color: #0b1929;
        color: #fff;
      }
      .summary-label {
        font-size: 10px;
        font-weight: 600;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: #64748b;
        margin-bottom: 8px;
      }
      .summary-card.primary .summary-label {
        color: rgba(255, 255, 255, 0.62);
      }
      .summary-value {
        font-family: Georgia, "Times New Roman", serif;
        font-size: 22px;
        font-weight: 400;
        letter-spacing: -0.02em;
        line-height: 1.15;
      }
      .summary-sub {
        margin-top: 6px;
        font-size: 11.5px;
        color: #64748b;
      }
      .summary-card.primary .summary-sub {
        color: rgba(255, 255, 255, 0.7);
      }
      .intro {
        margin: 0 0 18px;
        color: #3d5a73;
        font-size: 13px;
      }
      .section-title {
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        color: #2e6bad;
        margin: 0 0 10px;
      }
      table.ledger {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 8px;
      }
      table.ledger th {
        text-align: left;
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: #2e6bad;
        padding: 0 8px 10px 0;
        border-bottom: 1px solid #dbe5ef;
      }
      table.ledger th.num,
      table.ledger td.num {
        text-align: right;
        white-space: nowrap;
      }
      table.ledger td {
        padding: 12px 8px 12px 0;
        border-bottom: 1px solid #eef2f6;
        vertical-align: top;
        color: #0b1929;
      }
      .strong { font-weight: 600; }
      .muted { color: #64748b; font-size: 11.5px; margin-top: 3px; }
      .credit { color: #15803d; font-weight: 600; }
      .debit { color: #0b1929; font-weight: 600; }
      .status {
        display: inline-block;
        padding: 3px 8px;
        border-radius: 999px;
        background: #f1f5f9;
        font-size: 11px;
        font-weight: 600;
      }
      .empty-row {
        text-align: center;
        color: #64748b;
        padding: 28px 8px !important;
      }
      .totals {
        display: flex;
        justify-content: flex-end;
        margin-top: 18px;
        padding-top: 14px;
        border-top: 1px solid #e2e8f0;
      }
      .totals-rows {
        min-width: 280px;
      }
      .totals-row {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        gap: 28px;
        padding: 5px 0;
        font-size: 12.5px;
      }
      .totals-row .label { color: #64748b; }
      .totals-row .value { font-weight: 700; text-align: right; white-space: nowrap; }
      .totals-row.closing {
        margin-top: 8px;
        padding-top: 12px;
        border-top: 1px solid #cbd5e1;
      }
      .totals-row.closing .label {
        color: #0b1929;
        font-weight: 700;
      }
      .totals-row.closing .value {
        font-family: Georgia, "Times New Roman", serif;
        font-size: 17px;
        font-weight: 400;
        color: #0b1929;
      }
      .footer {
        margin-top: 28px;
        padding-top: 14px;
        border-top: 1px solid #e2e8f0;
        font-size: 11px;
        color: #94a3b8;
        line-height: 1.55;
      }
      .footer strong { color: #64748b; }
      @media print {
        body { background: #fff; padding: 0; }
        .toolbar { display: none !important; }
        .sheet {
          max-width: none;
          border: none;
          border-radius: 0;
          box-shadow: none;
          padding: 0;
        }
      }
      @media (max-width: 800px) {
        .summary { grid-template-columns: 1fr 1fr; }
      }
    </style>
  </head>
  <body>
    <div class="toolbar">
      <button type="button" onclick="window.print()">Print / Save as PDF</button>
    </div>
    <div class="sheet">
      <header class="header">
        <div>
          <h1 class="brand">HansePay</h1>
          <p class="brand-sub">Account statement</p>
        </div>
        <div class="meta">
          <strong>${escapeHtml(title)}</strong>
          ${ownerHtml}
          ${identityHtml}
          ${managerHtml}
          <div class="meta-period">${escapeHtml(periodLabel)}</div>
          <div>${escapeHtml(filterLabel)}</div>
          <div>Generated ${escapeHtml(generatedAt)}</div>
        </div>
      </header>

      <div class="summary">
        <div class="summary-card">
          <div class="summary-label">Opening balance</div>
          <div class="summary-value">${escapeHtml(formatMoney(openingBalance, currency, { signed: openingBalance < 0 }))}</div>
          <div class="summary-sub">Derived from activity + current balance</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">Money in</div>
          <div class="summary-value">${escapeHtml(formatMoney(totalCredits, currency))}</div>
          <div class="summary-sub">${rows.filter((r) => r.credit > 0).length} credits</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">Money out</div>
          <div class="summary-value">${escapeHtml(formatMoney(totalDebits, currency))}</div>
          <div class="summary-sub">${rows.filter((r) => r.debit > 0).length} debits</div>
        </div>
        <div class="summary-card primary">
          <div class="summary-label">Closing balance</div>
          <div class="summary-value">${escapeHtml(closingLabel)}</div>
          <div class="summary-sub">Available ${escapeHtml(symbol || currency)}</div>
        </div>
      </div>

      <p class="intro">
        Statement for your ${escapeHtml(currency)} wallet
        ${accountIdentity ? ` · ${escapeHtml(accountIdentity.label)} ${escapeHtml(accountIdentity.value)}` : ''}.
        Includes ${rows.length} transaction${rows.length === 1 ? '' : 's'}
        (${escapeHtml(filterLabel.toLowerCase())}) from your HansePay activity history.
      </p>

      <h2 class="section-title">Transaction history</h2>
      <table class="ledger">
        <thead>
          <tr>
            <th>Date</th>
            <th>Details</th>
            <th>Status</th>
            <th class="num">Money in</th>
            <th class="num">Money out</th>
          </tr>
        </thead>
        <tbody>
          ${tableRows}
        </tbody>
      </table>

      <div class="totals">
        <div class="totals-rows">
          <div class="totals-row">
            <span class="label">Total money in</span>
            <span class="value credit">${escapeHtml(formatMoney(totalCredits, currency))}</span>
          </div>
          <div class="totals-row">
            <span class="label">Total money out</span>
            <span class="value debit">${escapeHtml(formatMoney(totalDebits, currency))}</span>
          </div>
          <div class="totals-row closing">
            <span class="label">Closing balance</span>
            <span class="value">${escapeHtml(closingLabel)}</span>
          </div>
        </div>
      </div>

      <footer class="footer">
        <strong>Important</strong><br />
        This statement summarises settled HansePay wallet activity for the ${escapeHtml(currency)} account.
        Failed, blocked, and in-transit transfers are excluded because they do not represent completed balance changes.
        Opening balance is estimated from the current available balance and listed activity. It is provided for your records and is not a certified bank statement.
      </footer>
    </div>
  </body>
</html>`;
}

export function openAccountStatementPdf(
  account,
  transactions = [],
  { filter = 'all', accountOwner = null, accountManager = null, contactPerson = null } = {},
) {
  if (!account) {
    throw new Error('Account statement is not available.');
  }

  const html = buildAccountStatementHtml(account, transactions, {
    filter,
    accountOwner,
    accountManager: accountManager || contactPerson,
  });
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const blobUrl = URL.createObjectURL(blob);
  const printWindow = window.open(blobUrl, '_blank', 'noopener,noreferrer');

  if (!printWindow) {
    URL.revokeObjectURL(blobUrl);
    throw new Error('Could not open account statement. Please allow popups for this site.');
  }

  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}
